import clsx from "clsx";
import {
  Bot,
  CalendarClock,
  FileCheck2,
  Loader2,
  Play,
  Sparkles,
  Zap,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import UsageBar from "./UsageBar";

import Tooltip from "@/common/components/tooltip/Tooltip";
import { useAuth } from "@/common/hooks/useAuth";
import { usePermissions } from "@/common/hooks/usePermissions";
import { useUsage } from "@/common/services/billing";

interface PlanUsageCardProps {
  collapsed: boolean;
  /** Render for a dark (rail) surface instead of the default light card. */
  dark?: boolean;
}

function prettyPlan(code: string | undefined): string {
  if (!code) return "Free";
  return code.charAt(0).toUpperCase() + code.slice(1);
}

/**
 * Renders three colour dots (one per daily metric) in collapsed mode so the
 * user still gets a visual signal without text. Tooltip on hover reveals the
 * numbers. Tone matches the `UsageBar` palette.
 */
function dotTone(used: number, limit: number | null): string {
  if (limit == null) return "bg-indigo-400";
  if (limit <= 0) return "bg-gray-300";
  const p = (used / limit) * 100;
  if (p >= 90) return "bg-red-500";
  if (p >= 60) return "bg-amber-500";
  return "bg-emerald-500";
}

const PlanUsageCard = ({ collapsed, dark = false }: PlanUsageCardProps) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { isAdmin: isOrgAdmin } = usePermissions();
  // Refetch every 60s while open so daily counters bump as work happens.
  const { data, isLoading, error } = useUsage({
    enabled: !!isAuthenticated,
    refetchIntervalMs: 60_000,
  });

  if (!isAuthenticated) return null;

  // 403/404 → user has no org yet; silently hide (e.g. invitees mid-onboarding).
  const status = (error as { response?: { status?: number } } | null)?.response
    ?.status;
  if (status === 403 || status === 404) return null;

  const snapshot = data?.data;
  const planName = prettyPlan(snapshot?.planCode);

  // ---- Collapsed mode: small icon + 3 dots ----
  if (collapsed) {
    const tooltip = snapshot ? (
      <div className="flex flex-col gap-1 text-[11px]">
        <span className="font-semibold">{planName} plan</span>
        <span>
          AI today:{" "}
          {(snapshot.limits.aiGenerationsPerDay ?? 1) <= 0
            ? "Not included"
            : `${snapshot.used.aiGenerationsPerDay} / ${snapshot.limits.aiGenerationsPerDay ?? "∞"}`}
        </span>
        <span>
          Auto runs today: {snapshot.used.automatedRunsPerDay} /{" "}
          {snapshot.limits.automatedRunsPerDay ?? "∞"}
        </span>
        <span>
          Scheduled today: {snapshot.used.scheduledRunsPerDay} /{" "}
          {snapshot.limits.scheduledRunsPerDay ?? "∞"}
        </span>
        <span>
          Test cases: {snapshot.used.testCases.toLocaleString()} /{" "}
          {snapshot.limits.maxTestCases ?? "∞"}
        </span>
      </div>
    ) : (
      "Plan usage"
    );

    return (
      <Tooltip content={tooltip} side="right" sideOffset={10}>
        <button
          type="button"
          onClick={() => navigate("/billing")}
          aria-label="Plan usage"
          className={clsx(
            "mx-auto my-2 flex h-10 w-10 flex-col items-center justify-center gap-0.5 rounded-md cursor-pointer",
            "text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-9 focus-visible:ring-offset-1",
          )}
        >
          <Zap size={14} strokeWidth={1.75} aria-hidden="true" />
          {snapshot ? (
            <div className="flex gap-0.5" aria-hidden="true">
              <span
                className={clsx(
                  "h-1 w-1 rounded-full",
                  dotTone(
                    snapshot.used.aiGenerationsPerDay,
                    snapshot.limits.aiGenerationsPerDay,
                  ),
                )}
              />
              <span
                className={clsx(
                  "h-1 w-1 rounded-full",
                  dotTone(
                    snapshot.used.automatedRunsPerDay,
                    snapshot.limits.automatedRunsPerDay,
                  ),
                )}
              />
              <span
                className={clsx(
                  "h-1 w-1 rounded-full",
                  dotTone(
                    snapshot.used.scheduledRunsPerDay,
                    snapshot.limits.scheduledRunsPerDay,
                  ),
                )}
              />
            </div>
          ) : null}
        </button>
      </Tooltip>
    );
  }

  // ---- Expanded mode ----
  return (
    <div className="px-2 pb-2 shrink-0">
      <div
        className={clsx(
          "rounded-lg border p-2.5",
          dark
            ? "border-rail-border bg-rail-foreground/[0.04]"
            : "border-gray-200/80 bg-gray-50/60",
        )}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <Sparkles
              size={12}
              strokeWidth={1.75}
              className={clsx(
                "shrink-0",
                dark ? "text-indigo-300" : "text-indigo-500",
              )}
              aria-hidden="true"
            />
            <span
              className={clsx(
                "text-[11px] font-semibold uppercase tracking-wide truncate",
                dark ? "text-rail-foreground/80" : "text-gray-700",
              )}
            >
              {planName} plan
            </span>
          </div>
          {isOrgAdmin && (
            <button
              type="button"
              onClick={() => navigate("/billing")}
              className={clsx(
                "text-[11px] font-medium hover:underline underline-offset-2 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-9 rounded-sm",
                dark
                  ? "text-rail-foreground/70 hover:text-rail-foreground"
                  : "text-indigo-600 hover:text-indigo-700",
              )}
            >
              Manage
            </button>
          )}
        </div>

        {isLoading || !snapshot ? (
          <div
            className={clsx(
              "flex items-center justify-center py-3",
              dark ? "text-rail-muted" : "text-gray-400",
            )}
            aria-label="Loading usage"
          >
            <Loader2 size={14} className="animate-spin" />
          </div>
        ) : (
          <div className="space-y-2">
            <UsageBar
              compact
              dark={dark}
              icon={
                <FileCheck2 size={11} strokeWidth={1.75} aria-hidden="true" />
              }
              label="Test cases"
              used={snapshot.used.testCases}
              limit={snapshot.limits.maxTestCases}
            />
            <UsageBar
              compact
              dark={dark}
              icon={<Bot size={11} strokeWidth={1.75} aria-hidden="true" />}
              label="AI today"
              used={snapshot.used.aiGenerationsPerDay}
              limit={snapshot.limits.aiGenerationsPerDay}
            />
            <UsageBar
              compact
              dark={dark}
              icon={<Play size={11} strokeWidth={1.75} aria-hidden="true" />}
              label="Auto runs today"
              used={snapshot.used.automatedRunsPerDay}
              limit={snapshot.limits.automatedRunsPerDay}
            />
            <UsageBar
              compact
              dark={dark}
              icon={
                <CalendarClock
                  size={11}
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
              }
              label="Scheduled today"
              used={snapshot.used.scheduledRunsPerDay}
              limit={snapshot.limits.scheduledRunsPerDay}
            />
          </div>
        )}

        {isOrgAdmin &&
        snapshot &&
        (snapshot.planCode === "free" || snapshot.planCode === "basic") ? (
          <button
            type="button"
            onClick={() => navigate("/billing")}
            className={clsx(
              "mt-2.5 w-full inline-flex items-center justify-center gap-1 rounded-md px-2 py-1.5 text-[11px] font-semibold cursor-pointer",
              "bg-indigo-600 text-white hover:bg-indigo-700 transition-colors",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-1",
            )}
          >
            <Sparkles size={11} strokeWidth={2} aria-hidden="true" />
            Upgrade plan
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default PlanUsageCard;
